import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { FilterPaymentTypeOptions } from "utils/Constant";
import { money } from "utils/helpers";

const CATEGORY_COLORS = [
  "#3b82f6",
  "#14b8a6",
  "#ef4444",
  "#f59e0b",
  "#6b7280",
  "#22c55e",
];

function SalesByCategoryCard({ items }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-gray-700">
        Sales by Category
      </h3>

      <div className="flex items-center gap-4">
        <ResponsiveContainer width={110} height={110}>
          <PieChart>
            <Pie data={items} dataKey="qty" innerRadius={30} outerRadius={50}>
              {items?.map((_, i) => (
                <Cell
                  key={i}
                  fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <table className="w-full text-xs">
          <tbody>
            {items?.map((item, i) => (
              <tr key={item.category}>
                <td className="py-0.5">
                  <span
                    className="mr-1 inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor:
                        CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                    }}
                  />
                  {item.category}
                </td>

                <td className="py-0.5 text-right text-gray-500">{item.qty}</td>

                <td className="py-0.5 text-right font-medium text-gray-900">
                  {money(item.revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentMethodCard({ items, totalTransactions, totalAmount }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        Payment Method Recap
      </h3>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-400">
            <th className="pb-2">Method</th>
            <th className="pb-2 text-right">Transactions</th>
            <th className="pb-2 text-right">Amount</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-50">
          {items?.map((item) => (
            <tr key={item.method}>
              <td className="py-1.5 text-gray-700">{FilterPaymentTypeOptions.find(
                              (x) => x.value === String(item.method)
                          )?.label}</td>

              <td className="py-1.5 text-right text-gray-500">
                {item.transactions}
              </td>

              <td className="py-1.5 text-right font-medium text-gray-900">
                {money(item.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex justify-between border-t border-gray-100 pt-2 text-xs font-semibold text-gray-700">
        <span>Total ({totalTransactions})</span>
        <span className="text-red-500">{money(totalAmount)}</span>
      </div>
    </div>
  );
}

function TopCustomerCard({ customers }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">Top Customer</h3>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-400">
            <th className="pb-2">Customer</th>
            <th className="pb-2 text-right">Purchase</th>
            <th className="pb-2 text-right">Rental</th>
            <th className="pb-2 text-right">Freq</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-50">
          {customers?.map((customer) => (
            <tr key={customer.customer}>
              <td className="py-1.5 text-gray-700">{customer.customer}</td>

              <td className="py-1.5 text-right text-gray-500">
                {money(customer.totalPurchase)}
              </td>

              <td className="py-1.5 text-right text-gray-500">
                {money(customer.totalRental)}
              </td>

              <td className="py-1.5 text-right text-gray-700">
                {customer.frequency}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* <button className="mt-3 w-full rounded-lg border border-emerald-200 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50">
        View All Customers
      </button> */}
    </div>
  );
}

function TopProductsCard({ products }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        Top 5 Best Selling Products
      </h3>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-400">
            <th className="pb-2">Product</th>
            <th className="pb-2 text-right">Qty</th>
            <th className="pb-2 text-right">Revenue</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-50">
          {products?.map((product) => (
            <tr key={product.productName}>
              <td className="py-1.5 text-gray-700">{product.productName}</td>

              <td className="py-1.5 text-right text-gray-500">{product.qty}</td>

              <td className="py-1.5 text-right font-medium text-gray-900">
                {money(product.revenue)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className="mt-3 w-full rounded-lg border border-emerald-200 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50">
        View All Products
      </button>
    </div>
  );
}

export default function RekapSection({ data, loading, error }) {
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-56 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <SalesByCategoryCard items={data?.salesByCategory} />

      <PaymentMethodCard
        items={data?.paymentMethodRecap}
        totalTransactions={data?.totalPaymentTransactions}
        totalAmount={data?.totalPaymentAmount}
      />

      <TopCustomerCard customers={data?.topCustomers} />

      {/* <TopProductsCard products={data?.topProductsTop5} /> */}
    </div>
  );
}
